import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRole } from '../api';

/**
 * Linear-style keyboard shortcuts.
 *
 * Active throughout the authed shell. Stays out of the way when the user is
 * typing in any input/textarea/contenteditable.
 *
 *   c         → /quick (Quick Command)
 *   g h       → /home
 *   g t       → /today (staff) or /home (owner)
 *   g s       → /staff (owner)
 *   g r       → /routines (owner)
 *   g m       → /memory (owner)
 *   g p       → /profile
 *   ?         → open shortcut help (TODO — for now no-op)
 */
export function ShortcutsProvider() {
  const nav = useNavigate();

  useEffect(() => {
    let lastG = 0;
    function onKey(e: KeyboardEvent) {
      // Don't capture when typing in inputs / editable surfaces.
      const target = e.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        (target?.isContentEditable ?? false);
      // Search is the one shortcut that has to work while typing — that is
      // the point of it — and the one that uses a modifier.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (!getRole()) return;
        e.preventDefault();
        nav('/search');
        return;
      }
      if (isTyping) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const role = getRole();
      if (!role) return;
      const now = Date.now();
      const inGSequence = now - lastG < 800;

      if (e.key === 'g') {
        lastG = now;
        return;
      }

      if (inGSequence) {
        lastG = 0;
        switch (e.key) {
          case 'h':
            nav(role === 'staff' ? '/today' : '/home');
            return;
          case 't':
            nav('/today');
            return;
          case 's':
            if (role === 'owner') nav('/staff');
            return;
          case 'r':
            if (role === 'owner') nav('/routines');
            return;
          case 'm':
            if (role === 'owner') nav('/memory');
            return;
          case 'p':
            nav('/profile');
            return;
        }
      }

      switch (e.key) {
        case 'c':
          if (role === 'owner') nav('/tasks?new=1');
          return;
        case '/':
          e.preventDefault();
          nav('/search');
          return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav]);

  return null;
}
